import React, { useEffect, useState } from 'react';
import { VehicleValueHill } from '../VehicleValueHill';
import {
  formatCanadianCurrency,
  getCMVStatus,
  validateCMVResponse,
  getMarketValueConfidence,
  getMarketValueAssumptions,
} from '../../util/cmv';
import css from './MarketValuePricingTool.module.css';

// Position labels shown when a seller's price falls into each CMV zone.
// Kept here (not duplicated) so every consumer of this tool shows identical wording.
const POSITION_LABELS = {
  'too-low': 'Below Market',
  'good-price': 'Competitive Price',
  'best-value': 'Market Value',
  'above-market': 'Above Market',
  'too-high': 'Significantly Above Market',
};

function parseMileage(mileage) {
  if (mileage == null) return null;
  const n =
    typeof mileage === 'string' ? parseInt(mileage.replace(/\D/g, ''), 10) : Number(mileage);
  return Number.isFinite(n) && n > 0 ? n : null;
}

const focusManualPriceInput = () => {
  const el = document.querySelector('input[name="price"]');
  el?.focus();
};

const CONFIDENCE_LABELS = {
  high: 'High confidence',
  moderate: 'Moderate confidence',
  low: 'Lower confidence',
};

const CONFIDENCE_EXPLANATIONS = {
  high: 'Priced from this exact vehicle’s VIN.',
  moderate: 'Priced from comparable listings for this year, make and model.',
  low: 'Based on limited comparable data — treat it as a rough guide.',
};

/**
 * Confidence + assumptions, shown under whichever estimate is on screen.
 * Every figure here is an estimate; nothing about it guarantees a sale price.
 */
function PricingGuidance({ data, inputs }) {
  const confidence = getMarketValueConfidence(data);
  const { used, notFactored } = getMarketValueAssumptions(data, inputs);

  return (
    <div className={css.guidance}>
      <div className={css.confidenceRow}>
        <span className={`${css.confidenceBadge} ${css[`confidence_${confidence}`]}`}>
          {CONFIDENCE_LABELS[confidence]}
        </span>
        <span className={css.confidenceExplain}>{CONFIDENCE_EXPLANATIONS[confidence]}</span>
      </div>

      <details className={css.assumptions}>
        <summary className={css.assumptionsSummary}>What this estimate is based on</summary>
        <div className={css.assumptionsBody}>
          <p className={css.assumptionsHeading}>Used</p>
          <ul className={css.assumptionsList}>
            {used.map(item => (
              <li key={item}>{item}</li>
            ))}
          </ul>
          {notFactored.length ? (
            <>
              <p className={css.assumptionsHeading}>Not factored in</p>
              <ul className={css.assumptionsList}>
                {notFactored.map(item => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </>
          ) : null}
        </div>
      </details>

      <p className={css.guidanceDisclaimer}>
        This is an estimate to help you price your listing — not an appraisal, an offer, or a
        guarantee that the vehicle will sell at any price. You set your own asking price.
      </p>
    </div>
  );
}

/**
 * MarketValuePricingTool
 *
 * Reusable VinAudit-powered pricing intelligence block. Fetches CMV
 * (Canadian Market Value) data for a VIN via GET /api/cmv, and renders
 * a suggested price + interactive market-value visualisation
 * (VehicleValueHill), plus live "where does this price sit" feedback.
 *
 * This is the single, centralized market-value service for the app —
 * any flow that needs VinAudit-backed pricing intelligence (seller
 * listing flow, dealer inventory, trade evaluations, buyer comparisons,
 * etc.) should render this component rather than calling /api/cmv
 * directly or re-implementing this logic.
 *
 * Props:
 *   vin                  — vehicle VIN (required to fetch data; renders nothing without it)
 *   mileage              — optional odometer reading (string or number, digits extracted)
 *   currentPriceAmount   — current asking price, in the SAME units as onUseSuggestedPrice reports
 *                           (sub-units, e.g. cents) — used to compute live pricing position
 *   onUseSuggestedPrice  — (amountSubunits) => void — called when the seller accepts the
 *                           suggested price, or drags the VehicleValueHill slider
 *   onMarketDataChange   — (cmvDataOrNull) => void — called whenever fresh CMV data loads,
 *                           or becomes unavailable (null) — parent can persist marketValueLow /
 *                           marketValueAverage / marketValueHigh / suggestedListingPrice / etc.
 */
export function MarketValuePricingTool({
  vin,
  mileage,
  year,
  make,
  model,
  trim,
  province,
  condition,
  currentPriceAmount,
  onUseSuggestedPrice,
  onMarketDataChange,
}) {
  const hasVin = !!vin && vin.length === 17;
  const hasYmm = !!year && !!make && !!model;

  const [status, setStatus] = useState(hasVin || hasYmm ? 'loading' : 'no-data');
  const [cmv, setCmv] = useState(null);
  const [estimate, setEstimate] = useState(null);

  const parsedMileage = parseMileage(mileage);

  useEffect(() => {
    let cancelled = false;

    // ── Path 1: VIN-specific value from VinAudit (most accurate) ──────────
    if (hasVin) {
      setStatus('loading');
      setEstimate(null);

      const params = new URLSearchParams({ vin });
      if (parsedMileage) params.set('mileage', String(parsedMileage));

      fetch(`/api/cmv?${params.toString()}`)
        .then(async res => {
          const data = await res.json().catch(() => null);
          if (!res.ok || !data || !data.success) {
            throw new Error(data?.error || `HTTP ${res.status}`);
          }
          return data.cmv;
        })
        .then(cmvData => {
          if (cancelled) return;
          if (!validateCMVResponse(cmvData)) {
            throw new Error('CMV response failed validation');
          }
          setCmv(cmvData);
          setStatus('success');
          onMarketDataChange?.({ ...cmvData, isVinSpecific: true });
        })
        .catch(err => {
          if (cancelled) return;
          // Required fallback per pricing-intelligence spec: never fabricate a
          // market value. Log it, let the seller continue with manual pricing.
          console.error('[MarketValuePricingTool] VinAudit unavailable:', err.message);
          setCmv(null);
          setStatus('unavailable');
          onMarketDataChange?.(null);
        });

      return () => {
        cancelled = true;
      };
    }

    // ── Path 2: no VIN, but year/make/model known ─────────────────────────
    // Falls back to the year/make/model comparables endpoint. Deliberately
    // NOT presented as equivalent to the VIN-specific value: this data has
    // a low/average/high range and no fair-value band, so it is rendered as
    // a range and labelled as an estimate rather than forced into the
    // VIN-shaped hill visual, which would imply precision the data lacks.
    if (hasYmm) {
      setStatus('loading');
      setCmv(null);

      const params = new URLSearchParams({ year: String(year), make, model });
      if (trim) params.set('trim', trim);
      if (parsedMileage) params.set('mileage', String(parsedMileage));
      if (province) params.set('province', province);
      if (condition) params.set('condition', condition);

      fetch(`/api/market-comps?${params.toString()}`)
        .then(async res => {
          const data = await res.json().catch(() => null);
          if (!res.ok || !data || !data.success) {
            throw new Error(data?.error || `HTTP ${res.status}`);
          }
          return data;
        })
        .then(data => {
          if (cancelled) return;
          const v = data.valuation;
          const low = v?.retail_private_low_cad;
          const avg = v?.retail_private_avg_cad;
          const high = v?.retail_private_high_cad;
          // Same no-fabrication rule as the VIN path: if the comparables
          // service returned no usable numbers, say so rather than guessing.
          const usable = [low, avg, high].every(n => typeof n === 'number' && isFinite(n));
          if (!usable || !(low <= avg && avg <= high)) {
            throw new Error('market-comps returned no usable valuation');
          }
          const estimateData = {
            marketLow: low,
            estimatedValue: avg,
            marketHigh: high,
            vehicle: data.vehicle,
            location: data.location,
            marketIntelligence: data.market_intelligence || null,
            dataSource: data.powered_by || 'Canadian Vehicle Market Value',
            updatedAt: new Date().toISOString(),
            isVinSpecific: false,
          };
          setEstimate(estimateData);
          setStatus('estimate');
          onMarketDataChange?.(estimateData);
        })
        .catch(err => {
          if (cancelled) return;
          console.error('[MarketValuePricingTool] market comps unavailable:', err.message);
          setEstimate(null);
          setStatus('unavailable');
          onMarketDataChange?.(null);
        });

      return () => {
        cancelled = true;
      };
    }

    // ── Path 3: nothing to price against ──────────────────────────────────
    setStatus('no-data');
    setCmv(null);
    setEstimate(null);
    onMarketDataChange?.(null);
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vin, hasVin, hasYmm, parsedMileage, year, make, model, trim, province, condition]);

  // Previously this returned null, so a seller who skipped the VIN saw a bare
  // price field with no indication that market pricing was ever available.
  if (status === 'no-data') {
    return (
      <div className={css.root}>
        <div className={css.noData} role="status">
          <p className={css.noDataTitle}>Want help pricing this vehicle?</p>
          <p className={css.noDataBody}>
            Add your VIN — or the year, make and model — on the Details step and we&apos;ll show you
            what similar vehicles are selling for in Canada.
          </p>
        </div>
      </div>
    );
  }

  if (status === 'loading') {
    return (
      <div className={css.root}>
        <p className={css.loading}>Loading market pricing data…</p>
      </div>
    );
  }

  if (status === 'unavailable') {
    return (
      <div className={css.root}>
        <div className={css.unavailable} role="status">
          Market pricing data is temporarily unavailable. You may enter your asking price.
        </div>
      </div>
    );
  }

  // status === 'estimate' — year/make/model range, not a VIN-specific value.
  if (status === 'estimate') {
    const mi = estimate.marketIntelligence;
    return (
      <div className={css.root}>
        <div className={css.estimateCard}>
          <p className={css.estimateBadge}>Estimated range</p>
          <p className={css.suggestedLabel}>
            Similar {estimate.vehicle} listings{estimate.location ? ` in ${estimate.location}` : ''}
          </p>
          <p className={css.estimateRange}>
            {formatCanadianCurrency(estimate.marketLow)} –{' '}
            {formatCanadianCurrency(estimate.marketHigh)}
          </p>
          <p className={css.suggestedAmount}>
            {formatCanadianCurrency(estimate.estimatedValue)}
            <span className={css.estimateAvgLabel}> average</span>
          </p>
          <p className={css.explanation}>
            Based on the year, make and model — not this specific vehicle.{' '}
            <strong>Add your VIN on the Details step for a VIN-specific market value.</strong>
          </p>
          <div className={css.actionsRow}>
            <button
              type="button"
              className={css.acceptBtn}
              onClick={() => onUseSuggestedPrice?.(Math.round(estimate.estimatedValue * 100))}
            >
              Use Average Price
            </button>
            <button type="button" className={css.overrideBtn} onClick={focusManualPriceInput}>
              Enter Your Own Price
            </button>
          </div>
        </div>

        {mi ? (
          <div className={css.marketIntel}>
            {mi.active_listings != null ? (
              <div className={css.intelItem}>
                <span className={css.intelValue}>{mi.active_listings}</span>
                <span className={css.intelLabel}>active listings</span>
              </div>
            ) : null}
            {mi.sold_last_90_days != null ? (
              <div className={css.intelItem}>
                <span className={css.intelValue}>{mi.sold_last_90_days}</span>
                <span className={css.intelLabel}>sold in 90 days</span>
              </div>
            ) : null}
            {mi.days_on_market_avg != null ? (
              <div className={css.intelItem}>
                <span className={css.intelValue}>{mi.days_on_market_avg}</span>
                <span className={css.intelLabel}>avg. days on market</span>
              </div>
            ) : null}
            {mi.demand ? (
              <div className={css.intelItem}>
                <span className={css.intelValue}>{mi.demand}</span>
                <span className={css.intelLabel}>demand</span>
              </div>
            ) : null}
          </div>
        ) : null}

        <PricingGuidance
          data={estimate}
          inputs={{ vin, mileage: parsedMileage, province, condition }}
        />

        <p className={css.source}>
          Source: {estimate.dataSource} · Estimate only, not a VIN-specific valuation
        </p>
      </div>
    );
  }

  // status === 'success'
  const currentPriceDollars =
    currentPriceAmount != null ? currentPriceAmount / 100 : cmv.estimatedValue;
  const zone = getCMVStatus(
    currentPriceDollars,
    cmv.fairRangeLow,
    cmv.fairRangeHigh,
    cmv.marketLow,
    cmv.marketHigh
  );
  const positionLabel = POSITION_LABELS[zone];
  const showWarning = zone === 'too-high' || zone === 'too-low';

  const handleUseSuggested = () => {
    onUseSuggestedPrice?.(Math.round(cmv.estimatedValue * 100));
  };

  return (
    <div className={css.root}>
      <div className={css.suggestedCard}>
        <p className={css.suggestedLabel}>Suggested Listing Price</p>
        <p className={css.suggestedAmount}>{formatCanadianCurrency(cmv.estimatedValue)}</p>
        <p className={css.explanation}>Based on current market data for similar vehicles.</p>
        <div className={css.actionsRow}>
          <button type="button" className={css.acceptBtn} onClick={handleUseSuggested}>
            Accept Suggested Price
          </button>
          <button type="button" className={css.overrideBtn} onClick={focusManualPriceInput}>
            Enter Your Own Price
          </button>
        </div>
      </div>

      <VehicleValueHill
        marketLow={cmv.marketLow}
        marketHigh={cmv.marketHigh}
        fairRangeMin={cmv.fairRangeLow}
        fairRangeMax={cmv.fairRangeHigh}
        estimatedValue={cmv.estimatedValue}
        initialAskingPrice={currentPriceDollars}
        onPriceSelect={p => onUseSuggestedPrice?.(Math.round(p * 100))}
      />

      <div className={css.positionRow} aria-live="polite">
        Your price: <strong>{positionLabel}</strong>
      </div>
      {showWarning && (
        <div className={css.warning} role="alert">
          {zone === 'too-high'
            ? 'Your price is above the current estimated market range and may reduce buyer interest.'
            : 'Your price is below the current estimated market range — you may be leaving money on the table.'}
        </div>
      )}

      <PricingGuidance
        data={{ ...cmv, isVinSpecific: true }}
        inputs={{ vin, mileage: parsedMileage, province, condition }}
      />

      <p className={css.source}>
        Source: {cmv.dataSource || 'VinAudit'} · Updated{' '}
        {new Date(cmv.updatedAt).toLocaleDateString('en-CA')}
      </p>
    </div>
  );
}

export default MarketValuePricingTool;
